// F-W (2026-10-05): staff session expiry.
//
// The staff login cookie lives 2 hours (KL: keep it fixed). The SPA only read
// it at page load, so after expiry a page kept showing cached rows while
// fresh requests were refused (401) — a form then rendered blank, which
// looked like missing data. Now any session 401, or a guarded page found
// without the cookie, ends the session and sends the user to log in with
// "Your session has expired", returning them to where they were.

import Cookies from "js-cookie";

export const STAFF_COOKIES = [
  "staff_token",
  "staff_username",
  "staff_role",
  "staff_team_ids",
  "staff_department_id",
] as const;

/** 401 codes meaning "no valid session" — NOT wrong-password 401s
 *  (`auth.login.invalid`, `auth.change_password.invalid`). */
export function isSessionExpiredCode(code: string | undefined): boolean {
  return code === "auth.missing" || Boolean(code?.startsWith("auth.jwt."));
}

export function clearStaffCookies(): void {
  STAFF_COOKIES.forEach((name) => Cookies.remove(name));
}

export const hasStaffSessionCookie = (): boolean =>
  Boolean(Cookies.get("staff_token"));

const LOGIN_PATH = "/staff/login";

/** A return path is only honoured inside the staff app, never the login
 *  page itself and never protocol-relative ("//evil"). */
export function safeNext(next: string | null | undefined): string | null {
  if (!next || !next.startsWith("/staff/") || next.startsWith("//"))
    return null;
  if (next === LOGIN_PATH || next.startsWith(`${LOGIN_PATH}?`)) return null;
  return next;
}

export function expiredLoginUrl(next: string): string {
  const back = safeNext(next);
  return `${LOGIN_PATH}?expired=1${back ? `&next=${encodeURIComponent(back)}` : ""}`;
}

let redirecting = false;

/** End the session and go to the login page (a full load, so every cached
 *  query and the auth state start fresh). Runs once even when several
 *  requests fail together. Only acts on staff pages other than login. */
export function expireStaffSession(): void {
  const { pathname, search, hash } = window.location;
  if (!pathname.startsWith("/staff") || pathname.startsWith(LOGIN_PATH)) return;
  if (redirecting) return;
  redirecting = true;
  clearStaffCookies();
  window.location.assign(expiredLoginUrl(`${pathname}${search}${hash}`));
}

/** Test helper. */
export function _resetSessionRedirectGuard(): void {
  redirecting = false;
}
